import React, { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Button,
  Heading,
  Text,
  useDisclosure,
  Spinner,
  useToast,
  Divider,
} from '@chakra-ui/react';
import PaymentModal from '../components/PaymentModal';
import SubscriptionsTable from '../components/SubscriptionsTable';
import BillingsTable from '../components/BillingsTable';
import CSVDownloadIcon from '../components/CSVDownloadIcon';
import { useCallback } from 'react';

import {
  formatDateFromIsoString,
  getFromLocalStorage,
} from '../utils/helperServices';
import { toastTypes } from '../constants';
import UpdatePaymentMethod from '../components/UpdatePaymentMethod';
import DelinquentMakePaymentModal from '../components/DelinquentMakePaymentModal';
import { tokenizedAxios } from '../utils/axiosInterceptor';

const AccountManager = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: billingMethodsModalIsOpen,
    onOpen: billingMethodsModalOnOpen,
    onClose: billingMethodsModalOnClose,
  } = useDisclosure();
  const {
    isOpen: delinquentMakePaymentModalIsOpen,
    onOpen: delinquentMakePaymentModalOnOpen,
    onClose: delinquentMakePaymentModalOnClose,
  } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const userDetailDict = useRef();

  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsTableObj, setSubscriptionsTableObj] = useState([]);
  const [billingsTotalPages, setBillingsTotalPages] = useState(0);
  const [billingsTableData, setBillingsTableData] = useState([]);
  const [isBillingsLoading, setIsBillingsLoading] = useState(false);
  const [billingMethods, setBillingMethods] = useState([]);
  const [outstandingAmt, setOutstandingAmt] = useState(0.0);
  const [outstandingBillings, setOutstandingBillings] = useState([]);
  const [delinquentPayments, setDelinquentPayments] = useState(null);
  const toast = useToast();
  const [reload, setReload] = useState(false);
  const locationsToActivate = useRef([]);
  const didInitFetch = useRef(false);

  const fetchAccountData = useCallback(async () => {
    setIsLoading(true);
    try {
      const partnerId = userDetailDict.current.partnerID;
      const locationIds = userDetailDict.current.locations
        ?.map(({ ntwkid }) => ntwkid)
        .join(',');

      const response = await tokenizedAxios.get(
        `${process.env.REACT_APP_API_URL}/billing/subscriptions/account-data`,
        {
          params: {
            locationIds,
            partnerId,
          },
        }
      );

      const {
        subscriptions,
        billingMethods,
        unpaidTotals,
        outstandingAmt,
        outstandingBillings,
        locationsToActivate: pendingLocations,
      } = response.data._data;

      locationsToActivate.current = pendingLocations;
      setOutstandingAmt(outstandingAmt);
      setOutstandingBillings(outstandingBillings);
      setBillingMethods(billingMethods);
      setSubscriptions(subscriptions);
      setSubscriptionsTableObj(
        subscriptions.map(sub => ({
          ...sub,
          rate:
            sub.rateType === 'dollar'
              ? `$${sub.rate.toFixed(2)}`
              : `${sub.rate.toFixed(2)}%`,
          billDate: formatDateFromIsoString(sub.nextBillDate),
        }))
      );
      setDelinquentPayments(unpaidTotals);
      setIsLoading(false);
    } catch (error) {
      toast(error.isSessionExpired ? toastTypes.sessionExpired : toastTypes.dataFetchError);
      setIsLoading(false);
      console.error('Error fetching billing data:', error.message);
      return { error: true };
    }
  }, [toast]);

  const fetchTransactions = useCallback(async ({ offset = 0, limit = 25 }) => {
    setIsBillingsLoading(true);
    try {
      const partnerId = userDetailDict.current.partnerID;
      const locationIds = userDetailDict.current.locations
        ?.map(({ ntwkid }) => ntwkid)
        .join(',');

      const response = await tokenizedAxios.get(
        `${process.env.REACT_APP_API_URL}/billing/invoices/transactions`,
        {
          params: {
            partnerId,
            locationIds,
            skip: offset === 0 ? 1 : offset,
            limit,
            sortDesc: true,
          },
        }
      );

      const {
        data,
        total,
        totalPages,
        limit: respLimit,
      } = response.data._data || {};

      const billings = (data || []).map(t => ({
        ...t,
        amount: `$${t.amount}`,
        dateTime: formatDateFromIsoString(t.createdOn),
      }));

      setBillingsTableData(billings);
      setBillingsTotalPages(
        totalPages || Math.ceil((total || 0) / (respLimit || limit || 25))
      );

      return { data, total, totalPages };
    } catch (error) {
      if (error.isSessionExpired) toast(toastTypes.sessionExpired);
      return { error: true };
    } finally {
      setIsBillingsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    // fetchAccountDataFromPhp();

    if (didInitFetch.current) return;
    didInitFetch.current = true;

    const { userDetailDict: localStorageUserDetailDict } = getFromLocalStorage([
      'userDetailDict',
    ]);
    if (!localStorageUserDetailDict) {
      console.error('UserDetailDict not found!');
      return;
    }
    userDetailDict.current = localStorageUserDetailDict;
    fetchAccountData();
    fetchTransactions({ offset: 1, limit: 25 });
  }, [fetchAccountData, fetchTransactions]);

  useEffect(() => {
    if (reload) {
      // fetchAccountDataFromPhp();
      fetchAccountData();
      fetchTransactions({ offset: 1, limit: 25 });
      setReload(false);
    }
  }, [reload, fetchAccountData, fetchTransactions]);

  const handleBillingsPageChange = useCallback(({ pageIndex, pageSize }) => {
    fetchTransactions({ offset: pageIndex, limit: pageSize });
  }, [fetchTransactions]);


  return (
    <Flex direction="column" w={'95%'} alignSelf="center" mb={20}>
      <Heading size="xl" m={5} alignSelf={'center'}>
        Account Management
      </Heading>
      {isLoading ? (
        <Spinner
          size="xl"
          alignSelf={'center'}
          mt={20}
          label="loading..."
          thickness="4px"
        />
      ) : (
        <>
          {delinquentPayments?.totalToPay ? (
            <Flex
              w="100%"
              alignItems={'center'}
              justifyContent="center"
              wrap={'wrap'}
              rowGap={3}
            >
              <Text color="tomato" fontSize={'lg'} fontWeight="semibold">
                Overdue Amount: ${`${delinquentPayments.totalToPay}`}
              </Text>
              <Button
                size="sm"
                ml={3}
                onClick={() => delinquentMakePaymentModalOnOpen()}
                isLoading={isLoading}
              >
                Make Payment
              </Button>
              <Divider orientation="vertical" mx={2} color="black" height={6} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => billingMethodsModalOnOpen()}
                isLoading={isLoading}
              >
                Add Billing Method
              </Button>
            </Flex>
          ) : (
            <Flex
              w="100%"
              alignItems={'center'}
              justifyContent="center"
              wrap={'wrap'}
              rowGap={3}
            >
              <Text fontSize={'lg'} fontWeight="semibold">
                Balance: ${`${outstandingAmt}`}
              </Text>
              <Button
                size="sm"
                ml={3}
                onClick={() => onOpen()}
                isLoading={isLoading}
                isDisabled={!(outstandingBillings.length > 0)}
              >
                Make Payment
              </Button>
              <Divider orientation="vertical" mx={2} color="black" height={6} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => billingMethodsModalOnOpen()}
                isLoading={isLoading}
              >
                Add Billing Method
              </Button>
            </Flex>
          )}
          <Flex mx={8} mt={10} mb={5} alignContent={'center'}>
            <Heading size="lg">Subscriptions</Heading>
            <CSVDownloadIcon
              tableName="subscriptions"
              tableData={subscriptionsTableObj}
            />
          </Flex>
          <SubscriptionsTable
            tableData={subscriptionsTableObj}
            subscriptions={subscriptions}
            billingMethods={billingMethods}
            setReload={setReload}
          />
          <Flex mx={8} mt={10} mb={5} alignItems={'center'}>
            <Heading size="lg">Billing History</Heading>
            <CSVDownloadIcon
              tableName="billings"
              tableData={billingsTableData}
              userDetailDict={userDetailDict.current}
            />
          </Flex>
          {billingsTableData?.length ? (
            <BillingsTable
              tableData={billingsTableData}
              totalPages={billingsTotalPages}
              onPageChange={handleBillingsPageChange}
              isLoading={isBillingsLoading}
            />
          ) : null}
          {isOpen && (
            <PaymentModal
              isOpen={isOpen}
              onClose={onClose}
              billingMethods={billingMethods}
              outstandingAmt={outstandingAmt}
              outstandingBillings={outstandingBillings}
              setReload={setReload}
              locationsToActivate={locationsToActivate.current}
            />
          )}
          {delinquentMakePaymentModalIsOpen && (
            <DelinquentMakePaymentModal
              isOpen={delinquentMakePaymentModalIsOpen}
              onClose={delinquentMakePaymentModalOnClose}
              billingMethods={billingMethods}
              delinquentPayments={delinquentPayments}
              setReload={setReload}
            />
          )}
          {billingMethodsModalIsOpen && (
            <UpdatePaymentMethod
              isOpen={billingMethodsModalIsOpen}
              onClose={billingMethodsModalOnClose}
              subscriptions={subscriptions}
              billingMethods={billingMethods}
              setReload={setReload}
              addOnly
            />
          )}
        </>
      )}
    </Flex>
  );
};

export default AccountManager;
